import { useQuery } from '@tanstack/react-query';
import { FormField, Input } from '@/shared/components';
import { useApi, useApiProblem } from '@/shared/api';
import { classifyChannel } from '@/features/calendar/calendarChannels';
import type { ParkingBookingSuggestion } from '@/shared/api';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { cn } from '@/shared/lib/cn';
import { parkingProblemText } from './parkingProblemText';

interface Props {
  propertyGroupId: string;
  from: string;
  to: string;
  search: string;
  onSearchChange: (value: string) => void;
  value: string;
  onChange: (bookingId: string) => void;
}

/**
 * Der Zeitraum kennt die Antwort meist schon: wer vom 8. bis 10. einen Platz vergibt, meint einen
 * der Gäste, die dann im Haus sind. Deshalb Vorschläge statt einer leeren Suchmaske — sortiert
 * nach Überschneidung, denn wer den ganzen Zeitraum abdeckt, ist der wahrscheinlichere Treffer.
 */
export function ParkingBookingPicker({
  propertyGroupId,
  from,
  to,
  search,
  onSearchChange,
  value,
  onChange,
}: Props) {
  const api = useApi();
  const rangeValid = Boolean(from && to && from < to);
  const begriff = useDebounce(search.trim());
  const { data, isLoading, error, refetch, isFetching, fetchStatus } = useQuery({
    queryKey: ['parking-booking-suggestions', propertyGroupId, from, to, begriff],
    queryFn: () => api.parking.bookingSuggestions(propertyGroupId, from, to, begriff),
    enabled: rangeValid,
  });
  const problem = useApiProblem(error ?? null);

  return (
    <div className="md:col-span-4">
      <FormField label="Gast" required hint="Vorgeschlagen werden die Gäste in diesem Zeitraum.">
        <Input
          value={search}
          placeholder="Name, Buchungsnummer oder Apartment"
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </FormField>
      <div role="radiogroup" aria-label="Gast" className="mt-2 max-h-56 space-y-1 overflow-y-auto">
        {!rangeValid ? (
          <p className="text-ink-soft text-sm">Zuerst den Zeitraum wählen.</p>
        ) : isLoading ? (
          <p className="text-ink-soft text-sm">Suche Gäste…</p>
        ) : fetchStatus === 'paused' && !data ? (
          <p role="alert" className="text-sealwax text-sm">
            Keine Verbindung – die Gäste im Zeitraum lassen sich nicht laden.
          </p>
        ) : error && !data ? (
          // Ein Fehler ist kein „niemand im Haus“ – sonst ordnet man den Platz dem Falschen zu.
          <p role="alert" className="text-sealwax flex flex-wrap items-center gap-2 text-sm">
            {parkingProblemText(problem, 'Die Gäste im Zeitraum konnten nicht geladen werden.')}
            <button
              type="button"
              className="text-ink min-h-11 font-bold underline"
              disabled={isFetching}
              onClick={() => void refetch()}
            >
              Erneut laden
            </button>
          </p>
        ) : !data || data.suggestions.length === 0 ? (
          <p className="text-ink-soft text-sm">
            {begriff
              ? `Kein Treffer für „${begriff}" in diesem Zeitraum.`
              : 'In diesem Zeitraum ist niemand im Haus.'}
          </p>
        ) : (
          data.suggestions.map((suggestion) => (
            <SuggestionRow
              key={suggestion.bookingId}
              suggestion={suggestion}
              selected={suggestion.bookingId === value}
              onSelect={() => onChange(suggestion.bookingId)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function SuggestionRow({
  suggestion,
  selected,
  onSelect,
}: {
  suggestion: ParkingBookingSuggestion;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'border-line w-full rounded-md border px-3 py-2 text-left text-sm transition',
        selected ? 'border-navy bg-card-alt' : 'hover:border-ink-soft',
      )}
    >
      <span className="text-ink block font-medium">
        {suggestion.guestName ?? suggestion.bookingReference ?? 'Buchung ohne Namen'}
      </span>
      <span className="text-ink-soft mt-0.5 block text-xs">
        {[
          suggestion.apartmentName,
          channelLabel(suggestion.channel),
          `${formatDay(suggestion.checkIn)}–${formatDay(suggestion.checkOut)}`,
          `${nights(suggestion.overlappingNights)} gemeinsam`,
        ]
          .filter(Boolean)
          .join(' · ')}
      </span>
    </button>
  );
}

/** Kanal deutsch („Booking.com“ statt `BookingCom`); ein unbekannter bleibt mit seinem Namen. */
function channelLabel(channel: string | null): string {
  if (!channel) return 'Direktbuchung';
  const meta = classifyChannel(channel);
  return meta.slug === 'otherOta' ? channel : meta.label;
}

function nights(count: number): string {
  return count === 1 ? '1 Nacht' : `${count} Nächte`;
}

function formatDay(iso: string): string {
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
}
